/**
 * CSV Generator Utility
 *
 * Low-level utilities for generating CSV and XLSX files from data.
 * Handles proper escaping, UTF-8 BOM for Excel compatibility, etc.
 */

/**
 * Generate CSV content from headers and rows
 * Handles proper escaping of special characters
 *
 * @param headers - Array of column header names
 * @param rows - Array of row objects (column name -> value)
 * @returns Blob containing the CSV file
 */
export function generateCSV(
  headers: string[],
  rows: Record<string, string>[]
): Blob {
  const lines: string[] = [];

  // Header row
  lines.push(headers.map(escapeCSVField).join(','));

  // Data rows
  rows.forEach((row) => {
    const values = headers.map((header) => escapeCSVField(row[header] || ''));
    lines.push(values.join(','));
  });

  const content = lines.join('\r\n');

  // Use UTF-8 BOM for Excel compatibility
  // This ensures Excel opens the file with correct encoding
  const BOM = '\uFEFF';
  return new Blob([BOM + content], { type: 'text/csv;charset=utf-8' });
}

/**
 * Escape a CSV field value according to RFC 4180
 * - Wrap in quotes if contains comma, quote, or newline
 * - Double any existing quotes
 *
 * @param value - The field value to escape
 * @returns Properly escaped CSV field
 */
function escapeCSVField(value: string | null | undefined): string {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  // Check if escaping needed
  if (
    stringValue.includes(',') ||
    stringValue.includes('"') ||
    stringValue.includes('\n') ||
    stringValue.includes('\r')
  ) {
    // Escape quotes by doubling them
    const escaped = stringValue.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  return stringValue;
}

/**
 * Generate XLSX file using SheetJS library
 * Library is lazy-loaded to avoid bundle bloat
 *
 * @param headers - Array of column header names
 * @param rows - Array of row objects
 * @param sheetName - Name for the worksheet
 * @returns Promise resolving to Blob containing XLSX file
 */
export async function generateXLSX(
  headers: string[],
  rows: Record<string, string>[],
  sheetName: string = 'Campaign'
): Promise<Blob> {
  // Dynamic import for code splitting - only loaded when XLSX is requested
  const XLSX = await import('xlsx');

  // Create worksheet data as array of arrays
  const wsData = [
    headers,
    ...rows.map((row) => headers.map((h) => row[h] || '')),
  ];

  // Create workbook and worksheet
  const ws = XLSX.utils.aoa_to_sheet(wsData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31)); // Max 31 chars for sheet name

  // Set column widths based on content
  const colWidths = headers.map((header, idx) => {
    const maxLength = Math.max(
      header.length,
      ...rows.map((row) => (row[header] || '').length)
    );
    return { wch: Math.min(maxLength + 2, 50) }; // Cap at 50 chars
  });
  ws['!cols'] = colWidths;

  // Generate binary array
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

  return new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
}

/**
 * Generate a sanitized filename for download
 *
 * @param title - Campaign title
 * @param platform - Platform name
 * @param format - File format (csv or xlsx)
 * @returns Sanitized filename
 */
export function generateFilename(
  title: string,
  platform: string,
  format: string
): string {
  // Remove special characters and replace spaces with hyphens
  const sanitized = title
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase()
    .slice(0, 50); // Limit length

  const date = new Date().toISOString().split('T')[0];
  return `${sanitized}-${platform}-${date}.${format}`;
}

/**
 * Trigger browser download of a Blob
 *
 * @param blob - The file content as Blob
 * @param filename - The filename for download
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the object URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 100);
}
